import { Link } from "react-router-dom";
import { Wrench, Cpu, ShoppingBag, Zap, Phone, MapPin, Clock, ChevronRight, Smartphone, Monitor, Battery, Search, Shield, Star, Tv, CircleDot, FileText, Wifi, Mail, BookOpen, ArrowRight } from "lucide-react";
import PublicHeader from "../components/PublicHeader";

const HERO_IMG = "https://images.unsplash.com/photo-1576613109753-27804de2cba8?w=1400&q=80";
const DIAG_IMG = "https://images.unsplash.com/photo-1562408590-e32931084e23?w=800&q=80";
const ACC_IMG = "https://images.unsplash.com/photo-1428223501723-d821c5d00ca3?w=800&q=80";
const BOUTIQUE_IMG = "https://images.unsplash.com/photo-1515940175183-6798529cb860?w=800&q=80";
const LOCATION_IMG = "https://images.unsplash.com/photo-1576705066230-17622f3398b6?w=800&q=80";

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7DB4FF] mb-4">Super U de Bergevin, Pointe-à-Pitre</p>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-none text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Réparation Experte &<br />Vente d'Accessoires
            </h1>
            <p className="text-lg text-zinc-300 leading-relaxed mb-10 max-w-lg">
              Déposez votre appareil en boutique, nous nous occupons du reste. Devis sous 48h et suivi en ligne de votre dossier.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#sav"
                className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-8 py-4 font-semibold hover:bg-[#0044CC] transition-all shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)]"
                data-testid="hero-cta-sav"
              >
                Déposer mon appareil <ChevronRight className="h-4 w-4" />
              </a>
              <Link
                to="/boutique"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur text-white border-2 border-white/30 px-8 py-4 font-semibold hover:bg-white/20 transition-all"
                data-testid="hero-cta-boutique"
              >
                Voir la boutique
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Marquee */}
      <div className="bg-[#09090B] py-4 overflow-hidden" data-testid="trust-marquee">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 mx-6">
              {["Devis sous 48h", "Pièces Garanties", "Neuf & Reconditionné", "Distributeur Canal+ Canalbox & TV Caraïbes", "Devis Offert"].map((text, j) => (
                <span key={j} className="text-sm font-bold uppercase tracking-[0.15em] text-white/80 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-[#0055FF] rounded-full" />
                  {text}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Canal+ Promotions */}
      <section className="py-12 md:py-16 bg-zinc-900">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <img
            src="/api/static-images/promo_canal_fibre_38.jpg"
            alt="La Fibre Canal+ - Offre DUO FIBRE à 38€/mois pendant 12 mois"
            className="w-full h-auto rounded-lg shadow-lg"
          />
          <p className="text-center text-zinc-500 text-xs mt-4">Distributeur agréé Canal+ Canalbox &amp; TV Caraïbes — Renseignements en boutique</p>
        </div>
      </section>

      {/* SAV Workflow - Comment ça marche */}
      <section id="sav" className="py-24 md:py-32 bg-white border-b border-zinc-200" data-testid="sav-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0055FF] mb-3">Prise en charge SAV</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#09090B] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Comment faire réparer votre appareil ?
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Rendez-vous directement à notre boutique pour déposer votre appareil. Après le dépôt, suivez l'avancement de votre réparation en ligne depuis votre espace client.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 md:gap-0 relative">
            {/* Connecting line desktop */}
            <div className="hidden md:block absolute top-16 left-[12.5%] right-[12.5%] h-0.5 bg-zinc-200" />

            {[
              {
                step: "1",
                icon: MapPin,
                title: "Venez en boutique",
                desc: "Rendez-vous au Super U de Bergevin à Pointe-à-Pitre avec votre appareil.",
                accent: "bg-[#0055FF]"
              },
              {
                step: "2",
                icon: Search,
                title: "Prise en charge",
                desc: "Nos techniciens examinent votre appareil. Diagnostic inclus (15€ tél., 19€ tablette, 22€ imprimante, 27€ PC).",
                accent: "bg-emerald-500"
              },
              {
                step: "3",
                icon: Wifi,
                title: "Suivi en ligne",
                desc: "Créez votre compte client et suivez en temps réel l'état de votre réparation depuis votre espace.",
                accent: "bg-amber-500"
              },
              {
                step: "4",
                icon: CircleDot,
                title: "Récupération",
                desc: "Vous êtes notifié quand c'est prêt. Revenez en boutique récupérer votre appareil réparé.",
                accent: "bg-[#09090B]"
              },
            ].map(({ step, icon: Icon, title, desc, accent }, i) => (
              <div key={step} className="flex flex-col items-center text-center px-6 py-8 relative">
                <div className={`w-14 h-14 ${accent} flex items-center justify-center text-white font-bold text-lg mb-5 relative z-10`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {step}
                </div>
                <Icon className="h-6 w-6 text-zinc-400 mb-3" />
                <h3 className="text-lg font-semibold text-[#09090B] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-[#E8F0FE] border border-[#0055FF]/20 p-6 md:p-8 text-center">
            <p className="text-lg font-semibold text-[#09090B] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Le dépôt se fait uniquement en boutique
            </p>
            <p className="text-sm text-zinc-600 mb-6 max-w-xl mx-auto">
              Pas de prise en charge à distance. Venez nous voir avec votre appareil, nous vous remettons un bon de dépôt et un accès à votre espace de suivi en ligne.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#contact" className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3 font-semibold text-sm hover:bg-[#0044CC] transition-colors" data-testid="sav-cta-contact">
                <MapPin className="h-4 w-4" /> Voir l'adresse et les horaires
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0055FF] mb-3">Nos Services</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#09090B] mb-12" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Tout pour vos appareils
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Large card */}
            <div className="md:col-span-8 bg-zinc-50 border border-zinc-200 p-8 md:p-12 relative overflow-hidden group">
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#0055FF] flex items-center justify-center">
                    <Wrench className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold tracking-tight text-[#09090B]" style={{ fontFamily: "'Outfit', sans-serif" }}>Réparation Tous Appareils</h3>
                </div>
                <p className="text-zinc-600 leading-relaxed mb-6 max-w-lg">
                  Smartphones, tablettes, ordinateurs portables et fixes. Nos techniciens qualifiés diagnostiquent et réparent vos appareils avec des pièces de qualité.
                </p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { icon: Smartphone, label: "Smartphones" },
                    { icon: Monitor, label: "PC & Mac" },
                    { icon: Cpu, label: "Tablettes" },
                  ].map(({ icon: Icon, label }) => (
                    <span key={label} className="flex items-center gap-2 bg-white border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-700">
                      <Icon className="h-4 w-4 text-[#0055FF]" /> {label}
                    </span>
                  ))}
                </div>
              </div>
              <img src={HERO_IMG} alt="" className="absolute right-0 bottom-0 w-1/3 h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity hidden lg:block" />
            </div>

            {/* Prise en charge + Exemple */}
            <div className="md:col-span-4 bg-white border border-zinc-200 p-6 sm:p-8 flex flex-col">
              <div>
                <div className="w-12 h-12 bg-emerald-500 flex items-center justify-center mb-4">
                  <Search className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold tracking-tight mb-3 text-[#09090B]" style={{ fontFamily: "'Outfit', sans-serif" }}>Prise en charge <span className="text-zinc-500 font-normal">— diagnostic inclus</span></h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-zinc-600">Téléphone</span><span className="font-bold text-[#09090B]">15€</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">Tablette</span><span className="font-bold text-[#09090B]">19€</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">Imprimante</span><span className="font-bold text-[#09090B]">22€</span></div>
                  <div className="flex justify-between"><span className="text-zinc-600">Ordinateur</span><span className="font-bold text-[#09090B]">27€</span></div>
                </div>
              </div>
              {/* Note */}
              <p className="text-[11px] text-zinc-400 mt-4">Si devis refusé, les frais de prise en charge sont conservés.</p>
            </div>

            {/* Small card: Accessories */}
            <div className="md:col-span-4 bg-white border border-zinc-200 p-8">
              <div className="w-12 h-12 bg-amber-500 flex items-center justify-center mb-4">
                <ShoppingBag className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-2 text-[#09090B]" style={{ fontFamily: "'Outfit', sans-serif" }}>Accessoires & Pièces</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Chargeurs, coques, câbles, écouteurs, clés USB, cartes mémoire et bien plus. Neufs et reconditionnés.
              </p>
            </div>

            {/* Small card: Guarantee */}
            <div className="md:col-span-4 bg-white border border-zinc-200 p-8">
              <div className="w-12 h-12 bg-[#0055FF] flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-2 text-[#09090B]" style={{ fontFamily: "'Outfit', sans-serif" }}>Garantie Pièces</h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                Toutes nos réparations sont couvertes par une garantie pièces et main-d'œuvre pour votre tranquillité.
              </p>
            </div>

            {/* Small card: Express */}
            <div className="md:col-span-4 bg-[#09090B] text-white p-8">
              <div className="w-12 h-12 bg-white/10 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-[#0055FF]" />
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Devis sous 48h</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                La plupart des devis sont établis sous 48h après dépôt en boutique. Suivi en temps réel depuis votre espace client.
              </p>
            </div>

            {/* Canal+, Canalbox & TV Caraïbes */}
            <div className="md:col-span-12 bg-gradient-to-r from-[#1a1a2e] to-[#16213e] text-white p-6 sm:p-8 md:p-10 flex flex-col items-center gap-6">
              <div className="w-14 h-14 bg-white/10 flex items-center justify-center shrink-0">
                <Tv className="h-7 w-7 text-white" />
              </div>
              <div className="text-center">
                <h3 className="text-xl sm:text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Distributeur officiel</h3>
                <p className="text-zinc-300 text-sm leading-relaxed max-w-xl mx-auto">
                  Retrouvez chez nous les offres et abonnements Canal+, Canalbox et TV Caraïbes. Souscription, installation et assistance technique sur place.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
                <span className="text-xl sm:text-2xl font-black tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>CANAL+</span>
                <span className="hidden sm:block w-px h-8 bg-white/30" />
                <span className="text-lg sm:text-xl font-black tracking-tight text-[#00a8e8]" style={{ fontFamily: "'Outfit', sans-serif" }}>CANALBOX</span>
                <span className="hidden sm:block w-px h-8 bg-white/30" />
                <span className="text-base sm:text-lg font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>TV Caraïbes</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tarifs */}
      <section id="tarifs" className="py-24 md:py-32 bg-white border-y border-zinc-200" data-testid="tarifs-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0055FF] mb-3">Tarifs</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#09090B] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Nos prix transparents
          </h2>
          <p className="text-zinc-600 mb-12 max-w-lg">Tarifs indicatifs. Un devis gratuit et personnalisé vous sera remis avant toute intervention.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Search, title: "Diagnostic",
                items: [
                  { label: "Diagnostic téléphone", price: "15€" },
                  { label: "Diagnostic tablette", price: "19€" },
                  { label: "Diagnostic imprimante", price: "22€" },
                  { label: "Diagnostic PC portable", price: "27€" },
                ]
              },
              {
                icon: Monitor, title: "Maintenance & Logiciel",
                items: [
                  { label: "Nettoyage virus / optimisation", price: "69€" },
                  { label: "Réinstallation Windows", price: "89€" },
                  { label: "Installation SSD", price: "79€" },
                  { label: "Transfert de données", price: "59€" },
                  { label: "Nettoyage interne PC", price: "49€" },
                ]
              },
              {
                icon: Smartphone, title: "Réparation & Installation",
                items: [
                  { label: "Changement écran téléphone", price: "69€" },
                  { label: "Changement batterie téléphone", price: "49€" },
                  { label: "Configuration imprimante", price: "49€" },
                  { label: "Installation Box / TV", price: "59€" },
                ]
              },
              {
                icon: Wrench, title: "Main d'œuvre",
                items: [
                  { label: "Main d'œuvre atelier", price: "55€/h" },
                  { label: "Intervention express", price: "29€" },
                ]
              },
            ].map(({ icon: Icon, title, items }) => (
              <div key={title} className="border border-zinc-200 p-0">
                <div className="flex items-center gap-3 p-6 border-b border-zinc-200 bg-zinc-50">
                  <Icon className="h-5 w-5 text-[#0055FF]" />
                  <h3 className="text-lg font-semibold text-[#09090B]" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
                </div>
                <div className="divide-y divide-zinc-100">
                  {items.map(({ label, price }) => (
                    <div key={label} className="flex justify-between items-center px-6 py-4">
                      <span className="text-sm text-zinc-700">{label}</span>
                      <span className={`text-sm font-bold ${price === "Gratuit" ? "text-emerald-600" : "text-[#09090B]"}`}>{price}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-zinc-500 font-medium mt-4 text-center">* Tarifs indicatifs, devis gratuit sur demande.</p>

          <div className="mt-8 bg-[#E8F0FE] border border-[#0055FF]/20 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-[#09090B]">Besoin d'un devis personnalisé ?</p>
              <p className="text-sm text-zinc-600">Déposez votre appareil en boutique et recevez un devis sous 24h.</p>
            </div>
            <a href="#contact" className="bg-[#0055FF] text-white px-6 py-3 font-semibold text-sm hover:bg-[#0044CC] transition-colors shrink-0" data-testid="tarifs-cta">
              Nous contacter
            </a>
          </div>
        </div>
      </section>

      {/* Boutique Preview */}
      <section id="boutique" className="py-24 md:py-32" data-testid="boutique-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0055FF] mb-3">Boutique en ligne</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#09090B] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Parcourez nos produits
              </h2>
              <p className="text-zinc-600 leading-relaxed mb-4">
                Câbles, chargeurs, coques, écouteurs, clés USB, batteries et bien plus. Des produits neufs et des bonnes affaires reconditionnées à prix réduit.
              </p>
              <ul className="space-y-3 mb-8">
                {["Accessoires neufs de qualité", "Produits reconditionnés garantis", "Retrait gratuit en boutique"].map(text => (
                  <li key={text} className="flex items-center gap-3 text-sm text-zinc-700">
                    <span className="w-5 h-5 bg-emerald-100 flex items-center justify-center flex-shrink-0"><Star className="h-3 w-3 text-emerald-600" /></span>
                    {text}
                  </li>
                ))}
              </ul>
              <Link
                to="/boutique"
                className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-8 py-4 font-semibold hover:bg-[#0044CC] transition-all shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(9,9,11,1)]"
                data-testid="boutique-cta"
              >
                Voir la boutique <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] bg-zinc-100 border border-zinc-200 overflow-hidden">
                <img src={BOUTIQUE_IMG} alt="Nos produits" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-[#09090B] text-white px-5 py-3 text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                NEUFS & RECONDITIONNÉS
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Livres Scolaires - Dépôt-Vente */}
      <section id="livres" className="py-24 md:py-32 bg-white border-t border-zinc-200" data-testid="livres-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0055FF] mb-3">Nouveau service</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#09090B] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Rachat &amp; Revente de livres scolaires
              </h2>
              <p className="text-zinc-600 leading-relaxed mb-6">
                Vos enfants ont fini leur année scolaire ? Donnez une seconde vie à leurs manuels !
                Proposez vos livres en ligne, nous les rachetons et les revendons à petit prix.
              </p>

              <div className="space-y-4 mb-8">
                <h3 className="font-semibold text-[#09090B]">Comment ça marche ?</h3>
                {[
                  { step: "1", title: "Proposez en ligne", desc: "Créez un compte et proposez votre livre avec une photo, le titre et votre prix souhaité." },
                  { step: "2", title: "On vous fait une offre", desc: "Nous évaluons votre livre et vous proposons un prix de rachat." },
                  { step: "3", title: "Déposez & encaissez", desc: "Venez déposer le livre à la boutique et repartez avec votre paiement. Un seul passage !" },
                ].map(({ step, title, desc }) => (
                  <div key={step} className="flex gap-4 items-start">
                    <div className="w-8 h-8 bg-[#0055FF] text-white flex items-center justify-center font-bold text-sm shrink-0">
                      {step}
                    </div>
                    <div>
                      <p className="font-medium text-[#09090B]">{title}</p>
                      <p className="text-sm text-zinc-500">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/login"
                className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-8 py-4 font-semibold hover:bg-[#0044CC] transition-all shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(9,9,11,1)]"
                data-testid="livres-cta"
              >
                Proposer un livre <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Exemple concret */}
            <div className="space-y-6">
              <div className="border border-zinc-200 bg-zinc-50 p-6" data-testid="livres-example">
                <div className="flex items-center gap-2 mb-4">
                  <BookOpen className="h-5 w-5 text-[#0055FF]" />
                  <h3 className="font-semibold text-[#09090B]" style={{ fontFamily: "'Outfit', sans-serif" }}>Exemple concret</h3>
                </div>
                <div className="space-y-4">
                  <div className="p-4 bg-white border border-zinc-200 rounded-lg">
                    <p className="font-medium text-[#09090B]">Transmath 3ème - Nathan 2024</p>
                    <p className="text-sm text-zinc-500 mt-1">Maths 3ème · Bon état</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-zinc-600">Prix neuf en librairie</span>
                      <span className="font-medium text-zinc-400 line-through">29.90 EUR</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-zinc-600">Vous recevez (rachat)</span>
                      <span className="font-bold text-emerald-600">5.00 EUR</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-zinc-600">Prix de revente en boutique</span>
                      <span className="font-bold text-[#0055FF]">12.00 EUR</span>
                    </div>
                  </div>
                  <div className="border-t border-zinc-200 pt-3">
                    <p className="text-xs text-zinc-500 text-center">
                      Tout le monde y gagne : vous récupérez de l'argent, l'acheteur économise <strong>60%</strong> sur le prix neuf.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 p-4 text-sm">
                <p className="font-medium text-emerald-800 mb-1">Livres acceptés</p>
                <p className="text-emerald-700">Manuels scolaires du CP à la Terminale, en bon état ou neuf. Éditions récentes uniquement (moins de 3 ans).</p>
              </div>

              <div className="flex items-center gap-3 text-sm text-zinc-500">
                <Phone className="h-4 w-4 text-[#0055FF] shrink-0" />
                <div>
                  <span>Une question ?</span>
                  <br className="sm:hidden" />
                  <a href="tel:0690802201" className="text-[#0055FF] font-medium hover:underline whitespace-nowrap"> 06 90 80 22 01</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section id="contact" className="py-24 md:py-32 bg-white border-t border-zinc-200" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0055FF] mb-3">Nous trouver</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#09090B] mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Venez nous voir
              </h2>

              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 bg-[#E8F0FE] flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5 text-[#0055FF]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#09090B]">Adresse</p>
                    <p className="text-zinc-600 text-sm">Super U de Bergevin<br />97110 Pointe-à-Pitre, Guadeloupe</p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 bg-[#E8F0FE] flex items-center justify-center shrink-0">
                    <Phone className="h-5 w-5 text-[#0055FF]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#09090B]">Téléphone</p>
                    <a href="tel:0590895635" className="text-[#0055FF] font-medium text-sm hover:underline">05 90 89 56 35</a> (fixe)
                    <br />
                    <a href="tel:0690574728" className="text-[#0055FF] font-medium text-sm hover:underline">06 90 57 47 28</a> (mobile)
                    <br />
                    <a href="tel:0690802201" className="text-[#0055FF] font-medium text-sm hover:underline">06 90 80 22 01</a> (mobile)
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 bg-[#E8F0FE] flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5 text-[#0055FF]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#09090B]">Email</p>
                    <a href="mailto:contact.bergevin@gmail.com" className="text-[#0055FF] font-medium text-sm hover:underline">contact.bergevin@gmail.com</a>
                    <br />
                    <a href="mailto:micro.pc971@gmail.com" className="text-[#0055FF] font-medium text-sm hover:underline">micro.pc971@gmail.com</a>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 bg-[#E8F0FE] flex items-center justify-center shrink-0">
                    <Clock className="h-5 w-5 text-[#0055FF]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#09090B] mb-1">Horaires</p>
                    <div className="text-sm text-zinc-600 space-y-1">
                      <div className="flex justify-between gap-8">
                        <span>Lundi, Mardi, Jeudi, Vendredi</span>
                        <span className="font-medium text-[#09090B]">8h - 14h</span>
                      </div>
                      <div className="flex justify-between gap-8">
                        <span>Samedi</span>
                        <span className="font-medium text-[#09090B]">9h - 13h</span>
                      </div>
                      <div className="flex justify-between gap-8">
                        <span>Mercredi, Dimanche</span>
                        <span className="font-medium text-red-500">Fermé</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] bg-zinc-100 border border-zinc-200 overflow-hidden">
                <img src={LOCATION_IMG} alt="Guadeloupe" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#09090B] text-white py-16" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <h2 className="text-3xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>MICRO MULTIMÉDIA</h2>
              <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">
                Votre spécialiste en réparation d'appareils électroniques et vente d'accessoires à Pointe-à-Pitre, Guadeloupe.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-4">Navigation</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li><a href="#services" className="hover:text-white transition-colors">Services</a></li>
                <li><a href="#tarifs" className="hover:text-white transition-colors">Tarifs</a></li>
                <li><Link to="/boutique" className="hover:text-white transition-colors">Boutique</Link></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-sm mb-4">Mon compte</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li><Link to="/login" className="hover:text-white transition-colors">Se connecter</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Créer un compte</Link></li>
                <li><a href="tel:0590895635" className="hover:text-white transition-colors">05 90 89 56 35 (fixe)</a></li>
                <li><a href="tel:0690574728" className="hover:text-white transition-colors">06 90 57 47 28 (mobile)</a></li>
                <li><a href="tel:0690802201" className="hover:text-white transition-colors">06 90 80 22 01 (mobile)</a></li>
                <li><a href="mailto:contact.bergevin@gmail.com" className="hover:text-white transition-colors">contact.bergevin@gmail.com</a></li>
                <li><a href="mailto:micro.pc971@gmail.com" className="hover:text-white transition-colors">micro.pc971@gmail.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <p className="text-xs text-zinc-500">© {new Date().getFullYear()} Micro Multimédia. Tous droits réservés.</p>
              <Link to="/mentions-legales" className="text-xs text-zinc-500 hover:text-white transition-colors">Mentions légales</Link>
              <Link to="/cgv" className="text-xs text-zinc-500 hover:text-white transition-colors">CGV</Link>
            </div>
            <p className="text-xs text-zinc-500">Super U de Bergevin, 97110 Pointe-à-Pitre</p>
          </div>
        </div>
      </footer>

      {/* Marquee animation CSS */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
